import {
    CheckmateCaseHistoryIcon,
    CheckmateEasyUpdateIcon,
} from '../../../../shared/CheckmateIcons';
import { ISameCaseCaseModel, ISameCaseModel } from '../../../../interfaces/ISameCaseModel';
import { Placeholder, Stack, Table } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import Common from '../../../../stores/Common';
import ExpandableRow from '../../../shared/ExpandableRow';
import { Link } from 'react-router-dom';
import { QuadCaseAuditControl } from '../../../shared/modals/QuadCaseAuditControl';
import { ToggleIcon } from '../../../../shared/ToggleIcon';
import classNames from 'classnames';

export function SameCasesCaseRow(props: {
    activeKeys: string[];
    easyUpdateAttempted: boolean;
    expandedByDefault?: boolean;
    sameCase: ISameCaseModel;
    onEasyUpdateClick: (caseGuid: string) => void;
}) {
    const { sameCase } = props;
    const [expanded, setExpanded] = useState<boolean>(false);
    const [showHistory, setShowHistoryModal] = useState<boolean>(false);
    const [caseHistoryGuid, setCaseHistoryGuid] = useState<string | undefined>(undefined);

    const handleToggle = async () => {
        setExpanded(!expanded);
    };

    const handleEasyUpdateForSameCase = async (sameCase: ISameCaseModel) => {
        if (!sameCase.guid) return;
        props.onEasyUpdateClick(sameCase.guid);
    };

    //// TODO: implement chat feature
    const handleChatIconClick = (userGuid: string) => {
        alert('show chat for user => ' + userGuid);
    };

    const handleCaseHistoryClick = (caseGuid: string) => {
        setShowHistoryModal(true);
        setCaseHistoryGuid(caseGuid);
    };

    const hasAtLeastOneEditableCase = sameCase.sameCaseCaseModels?.some((c) => c.canEdit);

    useEffect(() => {
        if (props.expandedByDefault || props.activeKeys.includes(sameCase.guid)) {
            setExpanded(true);
        } else if (!props.activeKeys.includes(sameCase.guid)) {
            setExpanded(false);
        }
    }, [props.expandedByDefault, props.activeKeys, sameCase.guid]);

    const easyUpdateAttmpted = props.easyUpdateAttempted;

    return (
        <>
            <tr style={{ verticalAlign: 'middle', cursor: 'pointer' }} onClick={handleToggle}>
                <td>
                    <ToggleIcon onClick={handleToggle} isOpen={expanded} />
                </td>
                <td>
                    <span className="nowrap">{sameCase.caseNumber}</span>
                </td>
                <td>{sameCase.caseName}</td>
                <td>{sameCase.jurisdiction}</td>
                <td className="text-right">
                    {hasAtLeastOneEditableCase && (
                        <CheckmateEasyUpdateIcon
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEasyUpdateForSameCase(sameCase);
                            }}
                            className={classNames('cursor-pointer', {
                                'color-gray': easyUpdateAttmpted,
                                'color-black': !easyUpdateAttmpted,
                            })}
                        />
                    )}
                </td>
            </tr>
            <ExpandableRow
                isExpanded={expanded}
                colSpan={5}
                customDivStyles={{ padding: '1em 2em 0em' }}
                customTrStyles={{ backgroundColor: '#f5f5f5' }}
            >
                <Table size="sm">
                    <thead>
                        <tr>
                            <th style={{ width: 200 }}>Zone</th>
                            <th style={{ width: 200 }}>Case Name</th>
                            <th style={{ width: 150 }}>Disease</th>
                            <th style={{ width: 125 }}>SQ Score</th>
                            <th style={{ width: 175 }}>Status</th>
                            <th style={{ width: 125 }}>Trial Date</th>
                            <th />
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {sameCase.sameCaseCaseModels?.map((caseGroupCase: ISameCaseCaseModel) => (
                            <tr key={caseGroupCase.caseGuid} style={{ verticalAlign: 'middle' }}>
                                <td>{caseGroupCase.detailedOrganizationName}</td>
                                <td>
                                    {caseGroupCase.canView ? (
                                        <Link
                                            to={`/zone/${caseGroupCase.organizationGuid}/case/${caseGroupCase.caseGuid}`}
                                            target="_blank"
                                        >
                                            {caseGroupCase.caseName}
                                        </Link>
                                    ) : (
                                        <>{caseGroupCase.caseName}</>
                                    )}
                                </td>
                                <td>
                                    <MaskableValue
                                        canView={caseGroupCase.canView}
                                        value={caseGroupCase.disease}
                                        width={100}
                                    />
                                </td>
                                <td>
                                    <MaskableValue
                                        canView={caseGroupCase.canView}
                                        value={caseGroupCase.sqScore}
                                        width={50}
                                    />
                                </td>
                                <td>
                                    <MaskableValue
                                        canView={caseGroupCase.canView}
                                        value={caseGroupCase.status}
                                        width={100}
                                    />
                                </td>
                                <td>
                                    <MaskableValue
                                        canView={caseGroupCase.canView}
                                        value={Common.dateFormat(caseGroupCase.trialDate)}
                                        width={80}
                                    />
                                </td>
                                <td>
                                    {!caseGroupCase.canView && (
                                        <A1A2Display
                                            caseGroupCase={caseGroupCase}
                                            onChatIconClick={() => console.log('coming soon')}
                                        />
                                    )}
                                </td>{' '}
                                <td className="text-right">
                                    {caseGroupCase.canView && (
                                        <CheckmateCaseHistoryIcon
                                            className="cursor-pointer"
                                            onClick={() =>
                                                handleCaseHistoryClick(caseGroupCase.caseGuid)
                                            }
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </ExpandableRow>

            {showHistory && (
                <QuadCaseAuditControl
                    cancelText={'OK'}
                    onCancel={() => setShowHistoryModal(false)}
                    onClose={() => setShowHistoryModal(false)}
                    open={showHistory}
                    params={{
                        caseGuid: caseHistoryGuid ?? '',
                    }}
                    title={'Case Audit History'}
                />
            )}
        </>
    );
}

const MaskableValue = (props: { value: string; canView: boolean; width?: number }) => {
    return (
        <div style={{ paddingRight: '2em' }}>
            {props.canView ? (
                <>{props.value}</>
            ) : (
                <Placeholder
                    xs={12}
                    bg="secondary"
                    style={{
                        borderRadius: 3,
                        opacity: '0.25',
                        cursor: 'default',
                        width: props.width,
                    }}
                />
            )}
        </div>
    );
};

const A1A2Display = (props: {
    caseGroupCase: ISameCaseCaseModel;
    onChatIconClick: (userGuid: string) => void;
}) => {
    const { caseGroupCase } = props;

    return (
        <div>
            <Stack>
                {caseGroupCase.caseManagerUser?.profile && (
                    <Stack direction="horizontal" gap={2} style={{ flexWrap: 'nowrap' }}>
                        <span className="bold">A1: </span>
                        <Stack direction="horizontal" gap={2} style={{ flexWrap: 'nowrap' }}>
                            <div>
                                {caseGroupCase.caseManagerUser?.profile?.lastName},{' '}
                                {caseGroupCase.caseManagerUser?.profile?.firstName}
                            </div>
                            {/* <i
                                className="fal fa-message fa-sm"
                                onClick={() => {
                                    const userGuid = caseGroupCase.caseManagerUser?.profile?.guid;

                                    if (userGuid) {
                                        props.onChatIconClick(userGuid);
                                    }
                                }}
                            /> */}
                        </Stack>
                    </Stack>
                )}
                {caseGroupCase.alternativeCaseManager?.profile && (
                    <Stack direction="horizontal" gap={2} style={{ flexWrap: 'nowrap' }}>
                        <span className="bold">A2: </span>
                        <Stack direction="horizontal" gap={2} style={{ flexWrap: 'nowrap' }}>
                            <div>
                                {caseGroupCase.alternativeCaseManager?.profile?.lastName},{' '}
                                {caseGroupCase.alternativeCaseManager?.profile?.firstName}
                            </div>
                            {/* <i
                                className="fal fa-message fa-sm"
                                onClick={() => {
                                    const userGuid =
                                        caseGroupCase.alternativeCaseManager?.profile?.guid;

                                    if (userGuid) {
                                        props.onChatIconClick(userGuid);
                                    }
                                }}
                            /> */}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </div>
    );
};
